import { useEffect, useRef, Fragment } from 'react';
import { styled, alpha, useTheme } from '@mui/material/styles';

import { Box, Grid } from '@mui/material';
import { PromisePool } from '@supercharge/promise-pool';
import FrontEndMainPeriodSelector from '../../components/landing/FrontEndMainPeriodSelector';
import FrontEndMainPyramidSelector from '../../components/landing/FrontEndMainPyramidSelector';
import FrontEndMainEntityDisplay from '../../components/landing/FrontEndMainEntityDisplay';
import Widget from '../../pages/admin/dashboard/items/Widget';
import PopulationWidget from '../../pages/admin/dashboard/items/PopulationWidget';
import Carousel from '../../components/carousel';
import { isNilOrBlank } from '../../utils/core';

import useResponsive from '../../hooks/useResponsive';
import { HEADER } from '../../config';

import NewGeoMap from '../../components/landing/NewGeoMap';

import { useDispatch, useSelector } from '../../redux/store';

import { getMapFeatures, getDashBoardBlock } from '../../redux/slices/frontend';

// ----------------------------------------------------------------------

const StyledRoot = styled('div')(({ theme }) => ({
  position: 'relative',
  [theme.breakpoints.up('md')]: {
    top: 0,
    left: 0,
    width: '100%',
    height: '100vh',
    position: 'fixed',
    backgroundColor: alpha(theme.palette.primary.darker, 0.12)
  }
}));

// ----------------------------------------------------------------------

export default function HomeHero() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isDesktop = useResponsive('up', 'md');
  const carouselRef = useRef(null);
  const { currentEntityId, currentPeriod, widgets } = useSelector((state) => state.frontend);

  const levelCarouselSettings = {
    dots: false,
    arrows: false,
    autoplay: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    rtl: Boolean(theme.direction === 'rtl'),
    fade: true,
    infinite: true,
    speed: 2000
  };

  const refreshHero = async () => {
    const jobs = [
      dispatch(
        getMapFeatures({
          entityId: currentEntityId,
          year: currentPeriod.end?.year || new Date().getFullYear(),
          quarter: currentPeriod.end?.quarter || 4
        })
      )
    ];

    if (!isNilOrBlank(widgets) && widgets.length > 0) {
      widgets.forEach((widget) =>
        jobs.push(
          dispatch(
            getDashBoardBlock({
              itemParams: {
                ...widget,
                entity_id: [currentEntityId],
                isLoading: widget.isLoading !== undefined ? widget.isLoading : false,
                touched: false,
                data: widget.data !== undefined ? widget.data : undefined,
                period: {
                  start: {
                    index: 0,
                    value: 1,
                    quarter: 1,
                    year: 2015,
                    startDate: '2015-01-01',
                    endDate: '2015-03-31'
                  },
                  end: {
                    index: 1,
                    value: 1,
                    quarter: 1,
                    year: 2015,
                    startDate: '2015-01-01',
                    endDate: '2015-03-31'
                  }
                }
              }
            })
          )
        )
      );
    }

    await PromisePool.for(jobs)
      .withConcurrency(jobs.length)
      .process(async (job) => job);
  };

  useEffect(() => {
    if (!isNilOrBlank(currentEntityId) && !isNilOrBlank(currentPeriod)) {
      refreshHero();
    }
  }, [currentEntityId, currentPeriod]); // eslint-disable-line

  return (
    <>
      <StyledRoot>
        <Box sx={{ height: 1 }}>
          <Grid container spacing={0} sx={{ height: 1 }}>
            {isDesktop && (
              <Grid item xs={12} md={8.5} sx={{ height: 1, paddingTop: 8 }}>
                <NewGeoMap />
              </Grid>
            )}

            <Grid
              item
              xs={12}
              md={3.5}
              sx={{ height: 1, paddingLeft: 1, paddingRight: 1, mt: `${HEADER.H_MAIN_DESKTOP}px` }}
            >
              <FrontEndMainPeriodSelector />
              {!isDesktop && <FrontEndMainPyramidSelector hasBgColor hasPadding />}
              <FrontEndMainEntityDisplay />
              {!isNilOrBlank(widgets) && widgets.length > 0 && !isNilOrBlank(currentEntityId) && (
                <>
                  {widgets.slice(0, 2).map((widget, wIndex) => (
                    <Fragment key={`frontend-widget-${widget.id}-${wIndex}`}>
                      {widget.format === 'PopulationWidget' && (
                        <PopulationWidget title={widget.title} params={widget} />
                      )}
                      {widget.format === 'Widget' && <Widget title={widget.title} params={widget} />}
                    </Fragment>
                  ))}
                  <Carousel ref={carouselRef} {...levelCarouselSettings}>
                    {widgets.slice(2).map((widget, wIndex) => (
                      <Fragment key={`frontend-widget-${widget.id}-${wIndex}`}>
                        {widget.format === 'PopulationWidget' && (
                          <PopulationWidget title={widget.title} params={widget} />
                        )}
                        {widget.format === 'Widget' && <Widget title={widget.title} params={widget} />}
                      </Fragment>
                    ))}
                  </Carousel>
                </>
              )}
            </Grid>
          </Grid>
        </Box>
      </StyledRoot>
      <Box sx={{ height: { md: '100vh' } }} />
    </>
  );
}
